import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import meApi from 'api/modules/meApi';
import auth from 'api/core/auth';

/**
 * TenantWarehouseGuard
 * - URL에서 tenant/warehouse 추출
 * - UserContext와 동기화
 * - 유효하지 않으면 fallback 처리
 */
const TenantWarehouseGuard = ({ children }) => {
  const { tenant, warehouse } = useParams();
  const navigate = useNavigate();

  const {
    user,
    isLoading, // Use isLoading from context
    selectedTenant,
    selectedWarehouse,
    setSelectedTenant,
    setSelectedWarehouse,
    refresh,
  } = useUserContext();

  useEffect(() => {
    // Wait for the user context to finish loading
    if (isLoading) {
      return;
    }

    const syncContextWithUrl = async () => {
      try {
        // 1) 기본 유효성 검사
        if (!tenant || !warehouse) {
          console.error(
            '[TenantGuard] Invalid path! Missing tenant or warehouse.',
          );
          toast.error('Wrong path. Move to Login page');
          auth.clearAuth();
          navigate('/login', { replace: true });
          return;
        }

        // 2) 현재 me 정보 기준으로 유효성 체크
        // isLoading is false, so user should be settled
        if (!user) {
          console.error(
            '[TenantGuard] User context not available after load. Logging out.',
          );
          auth.clearAuth();
          navigate('/login', { replace: true });
          return;
        }
        const me = user;

        const tenants = me?.tenants || [];

        // For superusers, use tenant-specific warehouse permissions if available
        let warehouses = [];
        if (me?.is_superuser && tenant) {
          // Find the tenant in the user's tenants array
          const currentTenant = tenants.find(t =>
            t.domains?.some(d => d.domain === tenant),
          );

          // Use tenant-specific warehouse permissions if available
          if (currentTenant && currentTenant.warehouse_permissions) {
            warehouses = currentTenant.warehouse_permissions.map(
              p => p.warehouse,
            );
          } else {
            warehouses = me?.warehouse_permissions?.map(p => p.warehouse) || [];
          }
        } else {
          warehouses = me?.warehouse_permissions?.map(p => p.warehouse) || [];
        }

        const validTenant = tenants.find(t =>
          t.domains?.some(d => d.domain === tenant),
        );
        const validWarehouse = warehouses.find(w => w.domain === warehouse);
        if (validTenant && validWarehouse) {
          // URL 값이 유효 → Context 동기화
          let tenantChanged = false;
          let warehouseChanged = false;

          if (!selectedTenant || selectedTenant.id !== validTenant.id) {
            setSelectedTenant(validTenant);
            tenantChanged = true;
          }
          if (
            !selectedWarehouse ||
            selectedWarehouse.id !== validWarehouse.id
          ) {
            setSelectedWarehouse(validWarehouse);
            warehouseChanged = true;
          }

          // If tenant or warehouse changed, refresh the data
          if (tenantChanged || warehouseChanged) {
            // Small delay to ensure context is updated before refresh
            setTimeout(() => {
              refresh();
            }, 100);
          }

          return;
        }

        // 3) fallback 처리: me 다시 호출
        const freshMe = await meApi.getMe();
        const freshTenants = freshMe.tenants || [];

        // For superusers, use tenant-specific warehouse permissions if available
        let freshWarehouses = [];
        if (freshMe?.is_superuser && tenant) {
          // Find the tenant in the user's tenants array
          const currentTenant = freshTenants.find(t =>
            t.domains?.some(d => d.domain === tenant),
          );

          // Use tenant-specific warehouse permissions if available
          if (currentTenant && currentTenant.warehouse_permissions) {
            freshWarehouses = currentTenant.warehouse_permissions.map(
              p => p.warehouse,
            );
          } else {
            freshWarehouses =
              freshMe?.warehouse_permissions?.map(p => p.warehouse) || [];
          }
        } else {
          freshWarehouses =
            freshMe?.warehouse_permissions?.map(p => p.warehouse) || [];
        }

        if (freshTenants.length && freshWarehouses.length) {
          const fallbackTenant = freshTenants[0];
          const fallbackWarehouse = freshWarehouses[0];

          const fallbackUrl = `/${fallbackTenant.domains?.[0]?.domain}/${fallbackWarehouse.domain}`;
          toast.info(
            'No tenant/warehouse to access. Redirecting to fallback...',
          );
          navigate(fallbackUrl, { replace: true });
          return;
        }

        // 4) fallback 실패 → 로그아웃
        console.error('[TenantGuard] Fallback failed. Logging out.');
        toast.error('No tenant/warehouse to access.');
        auth.clearAuth();
        navigate('/login', { replace: true });
      } catch (err) {
        console.error('[TenantGuard] useEffect error', err);
        toast.error('Auth Failed');
        auth.clearAuth();
        navigate('/login', { replace: true });
      } finally {
        // console.log('[TenantGuard] useEffect finished');
      }
    };

    syncContextWithUrl();
  }, [
    isLoading, // Depend on the context loading state
    user,
    tenant,
    warehouse,
    navigate,
    selectedTenant,
    selectedWarehouse,
    setSelectedTenant,
    setSelectedWarehouse,
    refresh,
  ]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return children || <Outlet />;
};
export default TenantWarehouseGuard;
